import * as React from 'react'

import { DriverModel } from '@/app/models'

type FetchDriver = (params: { driverId: number }) => Promise<unknown>

export function useLazyFullDrivers(
  driverIds: string[],
  fetchDriver: FetchDriver,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true
  const [fullDrivers, setFullDrivers] = React.useState<
    Record<string, DriverModel | null>
  >({})
  const [loadingDriverIds, setLoadingDriverIds] = React.useState<Set<string>>(
    new Set(),
  )

  const fetchDriverRef = React.useRef(fetchDriver)
  fetchDriverRef.current = fetchDriver

  const driverIdsRef = React.useRef(driverIds)
  driverIdsRef.current = driverIds

  const cacheRef = React.useRef<Record<string, DriverModel | null>>({})
  const inFlightRef = React.useRef(new Set<string>())

  const reset = React.useCallback(() => {
    cacheRef.current = {}
    inFlightRef.current.clear()
    setFullDrivers({})
    setLoadingDriverIds(new Set())
  }, [])

  React.useEffect(() => {
    if (!enabled || driverIds.length === 0) {
      cacheRef.current = {}
      inFlightRef.current.clear()
      setFullDrivers({})
      setLoadingDriverIds(new Set())
      return
    }

    const nextCache: Record<string, DriverModel | null> = {}
    for (const id of driverIds) {
      if (id in cacheRef.current) {
        nextCache[id] = cacheRef.current[id]
      }
    }
    cacheRef.current = nextCache
    setFullDrivers({ ...nextCache })

    setLoadingDriverIds((prev) => {
      const selected = new Set(driverIds)
      const next = new Set([...prev].filter((id) => selected.has(id)))
      return next.size === prev.size ? prev : next
    })

    const toFetch = driverIds.filter(
      (id) => !(id in cacheRef.current) && !inFlightRef.current.has(id),
    )

    if (toFetch.length === 0) return

    toFetch.forEach((id) => inFlightRef.current.add(id))
    setLoadingDriverIds((prev) => new Set([...prev, ...toFetch]))

    toFetch.forEach(async (id) => {
      const parsedId = Number(id)
      if (Number.isNaN(parsedId)) {
        inFlightRef.current.delete(id)
        setLoadingDriverIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
        return
      }

      try {
        const details = await fetchDriverRef.current({ driverId: parsedId })
        if (!driverIdsRef.current.includes(id)) return

        const driver = (details as DriverModel) || null
        cacheRef.current = { ...cacheRef.current, [id]: driver }
        setFullDrivers((prev) => ({ ...prev, [id]: driver }))
      } catch {
        if (!driverIdsRef.current.includes(id)) return
        cacheRef.current = { ...cacheRef.current, [id]: null }
        setFullDrivers((prev) => ({ ...prev, [id]: null }))
      } finally {
        inFlightRef.current.delete(id)
        setLoadingDriverIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      }
    })
  }, [driverIds, enabled])

  return {
    fullDrivers: fullDrivers as Record<string, DriverModel>,
    loadingDriverIds,
    reset,
  }
}
